/**
 * Journal reversal scheduling.
 *
 * A reversal can be created three ways:
 *   - manual:            someone reverses it later, on demand (today's behaviour)
 *   - on-date:            an explicit future date is chosen at posting time
 *   - next-period-start:  the standard accrual pattern — reverse on day one of
 *                          the following month, so an estimate never survives
 *                          into the period where the real invoice lands
 *
 * This module only decides the reversal date and builds the reversed line set.
 * It does not run a background scheduler — nothing in this codebase executes
 * code on a timer. Instead, `isReversalDue` tells the UI when a scheduled
 * reversal's date has arrived, so JournalsView can surface it as an action the
 * user takes with one click. A person always fires the actual posting; the
 * schedule only decides when it becomes due.
 */

export type ReversalMode = "manual" | "on-date" | "next-period-start";

export interface JournalLineLike {
  id: string;
  accountCode: string;
  accountName?: string;
  debit: number;
  credit: number;
  costCenter?: string;
}

/** First day of the month following `date`. */
export const nextPeriodStart = (date: string): string => {
  const parsed = new Date(`${date.slice(0, 10)}T00:00:00Z`);
  const next = new Date(Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth() + 1, 1));
  return next.toISOString().slice(0, 10);
};

export interface ResolveReversalDateIssue {
  field: "scheduledDate";
  message: string;
}

/**
 * Resolves the concrete reversal date for a mode. Returns an issue rather than
 * throwing when an explicit date is required but missing or falls on or
 * before the journal date — a reversal cannot predate what it reverses.
 */
export const resolveReversalDate = (
  mode: ReversalMode,
  journalDate: string,
  explicitDate?: string
): { date?: string; issue?: ResolveReversalDateIssue } => {
  if (mode === "manual") return {};
  if (mode === "next-period-start") return { date: nextPeriodStart(journalDate) };

  if (!explicitDate) {
    return { issue: { field: "scheduledDate", message: "Choose a reversal date." } };
  }
  if (explicitDate <= journalDate) {
    return { issue: { field: "scheduledDate", message: "The reversal date must fall after the journal date." } };
  }
  return { date: explicitDate };
};

/** True once today has reached the scheduled date, so the reversal can fire. */
export const isReversalDue = (scheduledDate: string | undefined, today: string): boolean =>
  Boolean(scheduledDate) && today >= scheduledDate!;

/**
 * Builds the reversed line set — every debit and credit swapped, same
 * accounts, same cost centres. The caller wraps this in the journal record
 * (new id, number, status, source references) since those are UI/state
 * concerns, not arithmetic ones.
 */
export const reverseLines = <T extends JournalLineLike>(
  lines: T[],
  makeId: (sourceId: string) => string
): T[] =>
  lines.map((line) => ({
    ...line,
    id: makeId(line.id),
    debit: line.credit,
    credit: line.debit
  }));

export const REVERSAL_MODE_LABELS: Record<ReversalMode, string> = {
  manual: "Manual — reverse later on demand",
  "on-date": "On a specific date",
  "next-period-start": "First day of next month"
};

/** A short, human description of when a scheduled reversal will fire. */
export const describeReversalSchedule = (mode: ReversalMode, resolvedDate?: string): string => {
  if (mode === "manual") return "No reversal is scheduled. Reverse it manually whenever needed.";
  if (!resolvedDate) return "Reversal date not yet set.";
  return mode === "next-period-start"
    ? `Scheduled to reverse on ${resolvedDate} — the first day of the following month.`
    : `Scheduled to reverse on ${resolvedDate}.`;
};
